/**
 * 工程表の画面の部品が使うフック（#102）：画面の幅・2本指の拡大と縮小・人の列（#297）
 */
import { useEffect, useRef, useState } from "react";

/** 2本指で拡大・縮小（日の表示の1日の幅。docs/39 §5.2 の11） */
export const ZOOM_MIN = 12;
export const ZOOM_MAX = 48;
/**
 * 枠の中の2本指の拡大・縮小（touch イベント。passive にしないで、2本指のときだけスクロールを止める）。
 * 1日の幅を 12〜48px にし、指の真ん中の日が動かないように横の位置を合わせる。
 * 受け取った書類の原本（S3、kits/received/OriginalPane.tsx）も使う＝px は倍率（%）、範囲は min・max で渡す
 */
export function usePinchZoom(el: React.RefObject<HTMLDivElement | null>, px: number, onPx: (px: number) => void, left: number, min = ZOOM_MIN, max = ZOOM_MAX) {
  const st = useRef<{ d0: number; px0: number; day: number } | null>(null);
  const pxRef = useRef(px);
  pxRef.current = px;
  useEffect(() => {
    const box = el.current;
    if (!box) return;
    const dist = (t: TouchList) => Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
    const mid = (t: TouchList) => (t[0]!.clientX + t[1]!.clientX) / 2;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      const x = mid(e.touches) - box.getBoundingClientRect().left + box.scrollLeft - left;
      st.current = { d0: dist(e.touches), px0: pxRef.current, day: x / pxRef.current };
    };
    const onMove = (e: TouchEvent) => {
      const s0 = st.current;
      if (!s0 || e.touches.length !== 2) return;
      if (e.cancelable) e.preventDefault();
      const next = Math.round(Math.max(min, Math.min(max, (s0.px0 * dist(e.touches)) / s0.d0)));
      if (next === pxRef.current) return;
      const mx = mid(e.touches) - box.getBoundingClientRect().left;
      onPx(next);
      requestAnimationFrame(() => {
        box.scrollLeft = Math.max(0, s0.day * next + left - mx);
      });
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) st.current = null;
    };
    box.addEventListener("touchstart", onStart, { passive: true });
    box.addEventListener("touchmove", onMove, { passive: false });
    box.addEventListener("touchend", onEnd);
    box.addEventListener("touchcancel", onEnd);
    return () => {
      box.removeEventListener("touchstart", onStart);
      box.removeEventListener("touchmove", onMove);
      box.removeEventListener("touchend", onEnd);
      box.removeEventListener("touchcancel", onEnd);
    };
  }, [el, onPx, left, min, max]);
}

/** 画面の幅（px）が min 以上か。変わったら描き直す */
export function useWide(min: number): boolean {
  const q = `(min-width: ${min}px)`;
  const [on, setOn] = useState(() => (typeof window === "undefined" ? true : window.matchMedia(q).matches));
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    m.addEventListener("change", f);
    f();
    return () => m.removeEventListener("change", f);
  }, [q]);
  return on;
}


/**
 * 人の列（#297、docs/102 §2.4.1）：帯の ⋮「表示設定」の「人」。既定は出さない（{task}に1つの人数は日で人数が動く現場に合わない）。
 * ON にした人のブラウザに覚える（#102 からの `gb.sch.crewCol`＝on／off。前の「人数を入れた{schedule}だけ出す」はやめた）。
 * ON のときだけ 案件の{schedule}の「人」の列とスマホの人数の欄・全体の{schedule}の「その日の人数」「今週 延べ」を出す
 */
const CREW_KEY = "gb.sch.crewCol";
const readCrewOn = (): boolean => {
  try {
    return window.localStorage.getItem(CREW_KEY) === "on";
  } catch {
    return false;
  }
};
export function useCrewColumn(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => (typeof window === "undefined" ? false : readCrewOn()));
  const set = (v: boolean) => {
    setOn(v);
    try {
      window.localStorage.setItem(CREW_KEY, v ? "on" : "off");
    } catch {
      /* 覚えられなくても使える */
    }
  };
  return [on, set];
}
